import { motion } from "framer-motion";
import { LOGO_URLS, WHATSAPP_LINK, INSTAGRAM_URL, FACEBOOK_URL } from "../../lib/constants";
import { Instagram, Facebook, MessageCircle, MapPin, Clock, Phone, Heart } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { name: "Menú", href: "#productos" },
    { name: "Arma tu Caja", href: "#arma-tu-caja" },
    { name: "Quiz", href: "#quiz" },
    { name: "Regala", href: "#regala" },
  ];

  return (
    <footer className="bg-[#2D1810] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <img
              src={LOGO_URLS.transparent}
              alt="Urban Brownies"
              className="h-16 w-auto brightness-0 invert mb-4"
            />
            <p className="text-white/70 max-w-md mb-6">
              Brownies artesanales hechos con amor en Bucaramanga. 
              Cada brownie es un pedacito de felicidad que endulza tus momentos especiales.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#25D366] rounded-full hover:bg-[#20BD5A] transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gradient-to-br from-purple-600 to-pink-500 rounded-full hover:opacity-90 transition-opacity"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#1877F2] rounded-full hover:bg-[#166FE5] transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" strokeWidth={1.5} />
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="font-serif text-xl mb-4">Enlaces Rápidos</h4>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-white/70 hover:text-[#C88037] transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="font-serif text-xl mb-4">Contáctanos</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-white/70">
                <MapPin className="w-5 h-5 text-[#C88037] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>Bucaramanga y Área Metropolitana, Colombia</span>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <Phone className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                <a href="tel:+573107544509" className="hover:text-[#C88037] transition-colors">
                  +57 310 754 4509
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/70">
                <Clock className="w-5 h-5 text-[#C88037] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p>Pedidos: 1-2 días de anticipación</p>
                  <p className="text-sm">Fechas especiales: con más anticipación</p>
                </div>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">
              © {currentYear} Urban Brownies. Todos los derechos reservados.
            </p>
            <p className="text-white/50 text-sm flex items-center gap-1">
              Hecho con <Heart className="w-4 h-4 text-[#D9381E] fill-[#D9381E]" strokeWidth={1.5} /> en Bucaramanga
            </p>
          </div>
        </div>
      </div>

      {/* Accepted Payment Methods */}
      <div className="bg-[#1A0F0A] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 text-white/50 text-sm">
            <span>Métodos de pago:</span>
            <span className="bg-white/10 px-3 py-1 rounded">Nequi</span>
            <span className="bg-white/10 px-3 py-1 rounded">Bancolombia</span>
            <span className="bg-white/10 px-3 py-1 rounded">Efectivo</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
